import React, { useState, useCallback, useMemo } from "react";
import BreakdownChartBase from "./BreakdownChartBase";
import ChartDrillDownModalV2 from "./ChartDrillDown/ChartDrillDownModalV2";
import { CHART_CONFIG, CHART_TYPES } from "./chartConfig";
import { filterTableColumnsConfigForSeverity, formatDateForAPI } from "../../../Helper/Analytics/AnalyticsHelper";
import { formatDateRangeWithSmartYear } from "../../../Helper/DatePicker/DateFormatters";
import { Grid2, Typography } from "@mui/material";
import BusinessImpactBanner from "../Components/BusinessImpactBanner";
import { useChartDrillDown } from "./hooks/useChartDrillDown";
import { useBmsMetaInfoFields, useBmsTableConfig } from "./useBmsTableConfig";

const TriggeredFaultBreakdownChart = ({ startDate, endDate, hierarchicalLocation, alertCategories, operationalStatus, globalFilters }) => {
  const [groupBy, setGroupBy] = useState("originCity");

  // Configure drill-down hook with chart-specific overrides
  // Columns come from the bmsAlertV2 org meta when it configures them, and from
  // chartConfig otherwise, so an unconfigured org is unaffected.
  const resolveTable = useBmsTableConfig();
  const resolveMetaInfo = useBmsMetaInfoFields();

  const drillDownConfig = useMemo(() => ({
    chartType: CHART_TYPES.TRIGGERED_FAULT_BREAKDOWN,
    csvFileNamePrefix: "BmsFaults_TriggeredFaultBreakdown",

    // Level 1: Custom groupBy (dynamic based on breakdown dimension)
    getLevel1GroupBy: (dataPoint) => {
      return [groupBy, "FaultSeverityLevelID"];
    },

    getLevel1Filters: (dataPoint, baseFilters) => {
      const groupByValue = dataPoint?.selectedItem?.label;
      let filters = [];
      filters.push({ name: "errorDescription", values: [dataPoint.faultName] });
      filters.push({ name: "FaultSeverityName", values: [dataPoint.severityName] });
      if (groupByValue?.toLowerCase() !== 'all') {
        filters.push({ name: groupBy, values: [groupByValue] });
      }
      return [...baseFilters, ...filters];
    },

    getLevel1Select: () => ["GeneratedFaultsCount"],
    getLevel1MetaInfo: () => resolveMetaInfo(["vehicleCount"], "triggeredFaultBreakdownSummaryTable"),


    getTableConfig: (level, dataPoint) => {
      const triggeredConfig = CHART_CONFIG[CHART_TYPES.TRIGGERED_FAULT_BREAKDOWN];
      const severity = dataPoint?.severityName?.toLowerCase()
      let table = triggeredConfig?.popupConfig?.summaryTable
      let emptyMessage = "No alerts found for the selected criteria"
      let title = (dataPoint) => dataPoint?.faultName || ""
      let tableKey = "triggeredFaultBreakdownSummaryTable"

      if (level === 2) {
        table = triggeredConfig?.popupConfig?.vehiclesTable;
        tableKey = "triggeredFaultBreakdownVehiclesTable"
        emptyMessage = "No vehicles found for the selected fault"
        title = table.title
      }

      // Only the columns come from org meta; title and empty message stay with
      // chartConfig, so configuring columns cannot blank a heading.
      const resolved = resolveTable(tableKey, table);
      return {
        title: title,
        tableColumnsConfig: filterTableColumnsConfigForSeverity(resolved.tableColumnsConfig, severity),
        tableColumnOrder: resolved.tableColumnOrder,
        emptyMessage: emptyMessage,
      };

    },


    getModalTitle: (dataPoint) => {
      const faultName = dataPoint?.faultName || "";
      const category = formatDateRangeWithSmartYear(startDate, endDate);
      const firstGroupBy = dataPoint?.groupBy?.label || "By City";
      const secondGroupBy = dataPoint?.selectedItem?.label || "";
      const severityName = dataPoint?.severityName;
      return (
        <Grid2 container gap={1} alignItems={"center"}>
          <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "28px" }}>
            {`Triggered Battery System Fault Breakdown / ${firstGroupBy} / ${secondGroupBy} / ${severityName}`}
            <Typography variant="fw500s16c000000" sx={{ lineHeight: "28px" }}>
              {' / '}{faultName}
            </Typography>
          </Typography>
          <Typography variant="fw500s14c686868" sx={{ lineHeight: "20px" }}>
            {'•'}
          </Typography>
          <Typography variant="fw500s14c686868" sx={{ lineHeight: "20px" }}>
            {category}
          </Typography>
        </Grid2>
      );
    },


    getCSVFileName: (dataPoint) => {
      const faultName = dataPoint?.faultName || "Faults";
      const severityName = dataPoint?.severityName || "All";
      const formattedStartDate = new Date(startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const formattedEndDate = new Date(endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      return `BmsFaults_TriggeredFaultBreakdown_${faultName}_${severityName}_${formattedStartDate}-${formattedEndDate}.csv`;
    },

    // Level 2: Custom filters
    getLevel2Filters: (level2Record, dataPoint, baseFilters) => {
      const groupByValue = level2Record?.xLabel;
      return [
        ...baseFilters,
        { name: groupBy, values: [groupByValue] },
        { name: "errorDescription", values: [dataPoint.faultName] },
        { name: "FaultSeverityName", values: [dataPoint.severityName] },
      ];
    },

    // Level 2: Custom select and metaInfo
    getLevel2Select: () => ["GeneratedFaultsCount"],
    getLevel2MetaInfo: () => resolveMetaInfo(["licensePlateNumber", "originCountry", "originState", "originCity", "originDepo", "pingStatus"], "triggeredFaultBreakdownVehiclesTable"),
    getLevel2GroupBy: () => ["batteryID", "FaultSeverityLevelID"],

    // Level 2: Custom CSV filename
    level2GetCSVFileName: (level2Record, dataPoint) => {
      const faultName = level2Record?.xLabel || level2Record?.faultName || "Fault";
      const severityName = dataPoint?.severityName || "All";
      const formattedStartDate = new Date(startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const formattedEndDate = new Date(endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      return `BmsFaults_Vehicles_${faultName}_${severityName}_${formattedStartDate}-${formattedEndDate}.csv`;
    },
  }), [startDate, endDate, groupBy, resolveTable, resolveMetaInfo]);

  // Use the drill-down hook
  const drillDown = useChartDrillDown({
    startDate,
    endDate,
    hierarchicalLocation,
    // The customer scope lives on the globalFilters object this chart already
    // receives, the same object its own chart request is scoped by.
    fleetIds: globalFilters?.fleets,
    // The depot half of the same scope, from the same object.
    onboardingLocations: globalFilters?.onboardingLocations,
    allOnboardingLocations: globalFilters?.allOnboardingLocations,
    alertCategories,
    operationalStatus,
    filters: [],
    config: drillDownConfig,
  });

  // Destructure stable handler to avoid dependency on entire drillDown object
  const { handleDataPointClick: drillDownHandleClick } = drillDown;

  // Handle data point click from BreakdownChartBase
  const handleDataPointClick = useCallback((clickedData, currentGroupBy) => {
    setGroupBy(currentGroupBy);
    drillDownHandleClick(clickedData);
  }, [drillDownHandleClick]);

  // Transform API response to chart data format
  const transformChartData = useCallback((responseData) => {
    const transformedChartData = responseData.map(item => {
      const chartItem = {
        alertName: item.xLabel,
        warning: 0,
        high: 0,
        critical: 0,
      };

      item.metricsArray.forEach(metric => {
        if (metric.type === "warning") {
          chartItem.warning = metric.value;
        } else if (metric.type === "high severity") {
          chartItem.high = metric.value;
        } else if (metric.type === "critical") {
          chartItem.critical = metric.value;
        }
      });

      return chartItem;
    });

    // Sort by total count (warning + high + critical) in descending order
    return transformedChartData.sort((a, b) => {
      const totalA = (a.warning || 0) + (a.high || 0) + (a.critical || 0);
      const totalB = (b.warning || 0) + (b.high || 0) + (b.critical || 0);
      return totalB - totalA; // Descending order
    });
  }, []);

  // Prepare chart options and series for ApexCharts
  const getChartConfig = useCallback((chartData) => {
    if (!chartData || chartData.length === 0) {
      return { series: [], options: {} };
    }

    const categories = chartData.map(item => item.alertName);

    // Store raw data for labels and tooltips
    const rawWarningData = chartData.map(item => item.warning || 0);
    const rawHighData = chartData.map(item => item.high || 0);
    const rawCriticalData = chartData.map(item => item.critical || 0);

    // Calculate raw totals for each stack
    const rawStackedTotals = rawWarningData.map((warning, index) =>
      warning + rawHighData[index] + rawCriticalData[index]
    );

    // Calculate max value from raw totals
    const maxValue = Math.max(...rawStackedTotals);

    // Set minimum bar size for clickability (3% of max value)
    const NO_VALUE_MIN_WIDTH = maxValue > 0 ? maxValue * 0.02 : 1;  // ~2% of max for zero values
    const MIN_WIDTH = maxValue > 0 ? maxValue * 0.03 : 1;          // ~3% of max as reasonable min length

    // Transform data: ensure visual bars are at least MIN_WIDTH
    // For stacked bars, we need to scale proportionally to maintain segment ratios
    const visualWarningData = rawWarningData.map((warning, index) => {
      const total = rawStackedTotals[index];
      if (total === 0) return NO_VALUE_MIN_WIDTH / 3; // Distribute min width across segments
      const visualTotal = Math.max(total, MIN_WIDTH);
      // Maintain proportion: visualSegment = (rawSegment / rawTotal) * visualTotal
      return (warning / total) * visualTotal;
    });

    const visualHighData = rawHighData.map((high, index) => {
      const total = rawStackedTotals[index];
      if (total === 0) return NO_VALUE_MIN_WIDTH / 3;
      const visualTotal = Math.max(total, MIN_WIDTH);
      return (high / total) * visualTotal;
    });

    const visualCriticalData = rawCriticalData.map((critical, index) => {
      const total = rawStackedTotals[index];
      if (total === 0) return NO_VALUE_MIN_WIDTH / 3;
      const visualTotal = Math.max(total, MIN_WIDTH);
      return (critical / total) * visualTotal;
    });

    // Calculate max value from visual totals for xAxis
    const visualStackedTotals = visualWarningData.map((warning, index) =>
      warning + visualHighData[index] + visualCriticalData[index]
    );
    const visualMaxValue = Math.max(...visualStackedTotals);
    const xAxisMax = visualMaxValue * 1.1; // Add 10% buffer for data labels

    const series = [
      {
        name: "Warning",
        data: visualWarningData,
      },
      {
        name: "High Severity",
        data: visualHighData,
      },
      {
        name: "Critical",
        data: visualCriticalData,
      },
    ];

    // Only return overrides - common config is handled by base component
    const options = {
      chart: {
        id: "triggered-fault-breakdown-chart", // Required for custom legend
        stacked: true,
      },
      plotOptions: {
        bar: {
          dataLabels: {
            total: {
              enabled: true,
              formatter: (value, opts) => {
                // Get actual total from raw data
                const dataPointIndex = opts.dataPointIndex;
                const actualTotal = rawStackedTotals[dataPointIndex];
                return actualTotal; // Show actual value, not visual value
              },
            }
          }
        },
      },
      colors: ["#FFBF3D", "#FF7A3D", "#E03939"],
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        categories: categories,
        max: xAxisMax, // Set maximum to ensure space for data labels
      },
      legend: {
        show: false, // Hide default legend to use custom RenderChartLegend
      },
      tooltip: {
        y: {
          formatter: function (val, opts) {
            // Get actual values from raw data for tooltip
            const dataPointIndex = opts.dataPointIndex;
            const seriesIndex = opts.seriesIndex;

            let actualValue = val;
            if (seriesIndex === 0) {
              actualValue = rawWarningData[dataPointIndex];
            } else if (seriesIndex === 1) {
              actualValue = rawHighData[dataPointIndex];
            } else if (seriesIndex === 2) {
              actualValue = rawCriticalData[dataPointIndex];
            }

            return actualValue + " faults";
          },
        },
      },
      grid: {
        padding: {
          bottom: 24
        }
      }
    };

    return { series, options };
  }, []);

  const config = useMemo(() => ({
    sidebarSelect: "GeneratedFaultsCount",
    chartSelect: "GeneratedFaultsCount",
    chartGroupBy: ["errorDescription", "FaultSeverityName"],
    transformChartData,
    getChartConfig,
    chartPadding: "24px 28px",
    chartWrapperStyles: {
      // Position tooltip below bars for horizontal bar chart
      '& .apexcharts-tooltip': {
        transform: 'translateY(50px) !important', // Move tooltip down below the bars
      },
    },
    customLegend: {
      chartId: "triggered-fault-breakdown-chart",
      series: [
        { name: "Warning", color: "#FFBF3D" },
        { name: "High Severity", color: "#FF7A3D" },
        { name: "Critical", color: "#E03939" },
      ],
      centered: true,
      label: "Fault Name",
    },
  }), [transformChartData, getChartConfig]);

  return (
    <>
      <BreakdownChartBase
        title="Triggered Battery System Fault Breakdown"
        height="582px"
        minVisibilityHeight={400}
        startDate={startDate}
        endDate={endDate}
        hierarchicalLocation={hierarchicalLocation}
        alertCategories={alertCategories}
        globalFilters={globalFilters}
        config={config}
        chartType="triggeredFaultBreakdown"
        onDataPointClick={handleDataPointClick}
      />
      {drillDown.getTableConfig(1) && (
        <ChartDrillDownModalV2
          open={drillDown.modalOpen}
          onClose={drillDown.handleModalClose}
          title={drillDown.getModalTitle}
          selectedDataPoint={drillDown.selectedDataPoint}
          tableConfigs={drillDown.getTableConfig(1)}
          getTableData={drillDown.getTableData}
          applyTableAction={drillDown.applyTableAction}
          getCSVFileName={drillDown.getCSVFileName}
          level2TableConfigs={drillDown.getTableConfig(2)}
          level2GetTableData={drillDown.level2GetTableData}
          level2ApplyTableAction={drillDown.level2ApplyTableAction}
          level2GetCSVFileName={drillDown.level2GetCSVFileName}
          level2Title={drillDown.level2Title}
          bannerComponent={
            <BusinessImpactBanner
              data={[
                { label: "Vehicles Offline", value: "-" },
                { label: "Avg. Downtime", value: "-" },
              ]}
            />
          }
        />
      )}
    </>
  );
};

export default TriggeredFaultBreakdownChart;
